import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ─── LED spectrum colours (agriculturally useful wavelengths) ────────────────
const LED = {
  deepRed:   '#ff1744',  // 660nm — flowering & fruiting
  red:       '#ff5252',  // 630nm — photosynthesis peak
  orange:    '#ff9100',  // 600nm — carotenoid synthesis
  yellow:    '#fdd835',  // 580nm — supplemental
  green:     '#00e676',  // 520nm — canopy penetration
  blue:      '#2979ff',  // 450nm — vegetative growth
  royalBlue: '#651fff',  // 430nm — chlorophyll absorption
  uvA:       '#aa00ff',  // 380nm — pest resistance, flavonoids
  farRed:    '#d50000',  // 730nm — phytochrome response
};


// ─── Intersection Observer hook (scroll-driven animations like Plenty.ag) ───
function useOnScreen(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Animated counter (stats that count up on scroll) ────────────────────────
function Counter({ end, suffix = '', duration = 2000 }: { end: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const { ref, visible } = useOnScreen(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}


// ─── Floating LED particles (30 rising orbs in spectrum colours) ─────────────
function LedParticles() {
  const colors = [LED.deepRed, LED.blue, LED.green, LED.uvA, LED.royalBlue, LED.red, LED.yellow, LED.orange];
  const particles = useMemo(() =>
    Array.from({ length: 40 }).map((_, i) => ({
      color: colors[i % colors.length],
      size: 2 + Math.random() * 5,
      left: Math.random() * 100,
      delay: Math.random() * 10,
      dur: 8 + Math.random() * 12,
    })), []);
  return (
    <div className="led-particles" aria-hidden="true">
      {particles.map((p, i) => (
        <div key={i} className="led-particle" style={{
          background: p.color,
          boxShadow: `0 0 ${p.size*4}px ${p.color}, 0 0 ${p.size*8}px ${p.color}50`,
          width: p.size, height: p.size,
          left: `${p.left}%`,
          animationDelay: `${p.delay}s`,
          animationDuration: `${p.dur}s`,
        }}/>
      ))}
    </div>
  );
}

// ─── Vertical Farm Tower (enhanced SVG with LED glow) ────────────────────────
function VerticalFarmTower() {
  const shelves = [
    { y: 40, led: LED.deepRed, nm: '660nm', plants: 'Tomatoes' },
    { y: 130, led: LED.blue, nm: '450nm', plants: 'Lettuce' },
    { y: 220, led: LED.uvA, nm: '380nm', plants: 'Basil' },
    { y: 310, led: LED.red, nm: '630nm', plants: 'Peppers' },
    { y: 400, led: LED.royalBlue, nm: '430nm', plants: 'Microgreens' },
  ];
  return (
    <svg viewBox="0 0 360 520" className="vf-tower" aria-hidden="true">
      <defs>
        <filter id="glow" x="-50%" y="-200%" width="200%" height="600%">
          <feGaussianBlur stdDeviation="10" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="bigGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="25"/>
        </filter>
        <linearGradient id="frameGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,60,30,0.15)"/>
          <stop offset="100%" stopColor="rgba(0,60,30,0.05)"/>
        </linearGradient>
      </defs>

      {/* Frame */}
      <rect x="40" y="10" width="280" height="500" rx="12" fill="none" stroke="url(#frameGrad)" strokeWidth="1.5"/>
      {/* Vertical struts */}
      <line x1="40" y1="10" x2="40" y2="510" stroke="rgba(0,60,30,0.1)" strokeWidth="1"/>
      <line x1="320" y1="10" x2="320" y2="510" stroke="rgba(0,60,30,0.1)" strokeWidth="1"/>
      <line x1="180" y1="10" x2="180" y2="510" stroke="rgba(0,60,30,0.06)" strokeWidth="0.5" strokeDasharray="4 8"/>

      {shelves.map((s, i) => (
        <g key={i}>
          {/* Shelf */}
          <rect x="50" y={s.y + 68} width="260" height="3" rx="1.5" fill="rgba(0,60,30,0.12)"/>

          {/* LED strip — main */}
          <rect x="60" y={s.y} width="240" height="4" rx="2" fill={s.led} opacity="0.9"
            className="vf-led-bar" style={{ animationDelay: `${i*0.4}s` }}/>
          {/* LED glow cone */}
          <polygon points={`60,${s.y+4} 300,${s.y+4} 280,${s.y+68} 80,${s.y+68}`}
            fill={s.led} opacity="0.04"/>
          {/* LED strip glow */}
          <rect x="60" y={s.y} width="240" height="4" rx="2" fill={s.led} opacity="0.5" filter="url(#glow)"
            className="vf-led-pulse" style={{ animationDelay: `${i*0.4}s` }}/>

          {/* Individual LED dots */}
          {Array.from({ length: 12 }).map((_, j) => (
            <circle key={j} cx={72 + j * 20} cy={s.y + 2} r="1.5" fill={s.led} opacity="0.8"
              className="led-dot-blink" style={{ animationDelay: `${i*0.3 + j*0.08}s` }}/>
          ))}

          {/* Plants */}
          {Array.from({ length: 8 }).map((_, j) => {
            const px = 80 + j * 28;
            const h = 18 + Math.random() * 20;
            const species = (i + j) % 4;
            return (
              <g key={j} className="vf-plant" style={{ animationDelay: `${i*0.3 + j*0.12}s` }}>
                {/* Stem */}
                <line x1={px} y1={s.y+66} x2={px} y2={s.y+66-h} stroke="#2e7d32" strokeWidth="1.2" opacity="0.8"/>
                {species === 0 && <>
                  <ellipse cx={px-5} cy={s.y+66-h+4} rx="7" ry="5" fill="#66bb6a" opacity="0.8"/>
                  <ellipse cx={px+5} cy={s.y+66-h+2} rx="7" ry="5" fill="#81c784" opacity="0.75"/>
                  <ellipse cx={px} cy={s.y+66-h-2} rx="6" ry="4.5" fill="#a5d6a7" opacity="0.7"/>
                </>}
                {species === 1 && <>
                  <ellipse cx={px-4} cy={s.y+66-h+3} rx="4.5" ry="6.5" fill="#43a047" opacity="0.8" transform={`rotate(-20 ${px-4} ${s.y+66-h+3})`}/>
                  <ellipse cx={px+4} cy={s.y+66-h+1} rx="4.5" ry="6.5" fill="#66bb6a" opacity="0.75" transform={`rotate(20 ${px+4} ${s.y+66-h+1})`}/>
                  <ellipse cx={px} cy={s.y+66-h-4} rx="3.5" ry="5.5" fill="#81c784" opacity="0.7"/>
                </>}
                {species === 2 && <>
                  {[-3,-1,1,3].map(k => (
                    <line key={k} x1={px+k} y1={s.y+66} x2={px+k*1.8} y2={s.y+66-h+2} stroke="#388e3c" strokeWidth="0.8" opacity="0.7"/>
                  ))}
                  {[-4,-1,2,5].map(k => (
                    <circle key={k} cx={px+k*1.8} cy={s.y+66-h+1} r="2.5" fill="#69f0ae" opacity="0.75"/>
                  ))}
                </>}
                {species === 3 && <>
                  <circle cx={px} cy={s.y+66-h-1} r="4" fill="#e53935" opacity="0.7"/>
                  <ellipse cx={px-4} cy={s.y+66-h+6} rx="5" ry="4" fill="#4caf50" opacity="0.7" transform={`rotate(-10 ${px-4} ${s.y+66-h+6})`}/>
                  <ellipse cx={px+4} cy={s.y+66-h+6} rx="5" ry="4" fill="#66bb6a" opacity="0.65" transform={`rotate(10 ${px+4} ${s.y+66-h+6})`}/>
                </>}
              </g>
            );
          })}

          {/* Labels */}
          <text x="328" y={s.y+20} fill="rgba(0,60,30,0.4)" fontSize="7.5" fontFamily="monospace">{s.nm}</text>
          <text x="328" y={s.y+32} fill="rgba(0,60,30,0.3)" fontSize="6.5" fontFamily="system-ui">{s.plants}</text>
        </g>
      ))}

      {/* Water drips */}
      {[0,1,2,3].map(i => (
        <circle key={i} cx={100 + i * 60} cy="0" r="2" fill={LED.blue} opacity="0.5"
          className="vf-drip" style={{ animationDelay: `${i*0.9}s` }}/>
      ))}
    </svg>
  );
}

// ─── Spectrum visualiser (interactive) ───────────────────────────────────────
function SpectrumVisualiser() {
  const bands = [
    { nm: 380, label: 'UV-A', color: LED.uvA, height: 45, use: 'Pest defence' },
    { nm: 430, label: 'Royal Blue', color: LED.royalBlue, height: 70, use: 'Chlorophyll-a' },
    { nm: 450, label: 'Blue', color: LED.blue, height: 85, use: 'Veg growth' },
    { nm: 520, label: 'Green', color: LED.green, height: 35, use: 'Canopy depth' },
    { nm: 580, label: 'Yellow', color: LED.yellow, height: 25, use: 'Supplemental' },
    { nm: 600, label: 'Orange', color: LED.orange, height: 40, use: 'Carotenoids' },
    { nm: 630, label: 'Red', color: LED.red, height: 90, use: 'Photosynthesis' },
    { nm: 660, label: 'Deep Red', color: LED.deepRed, height: 100, use: 'Flowering' },
    { nm: 730, label: 'Far Red', color: LED.farRed, height: 55, use: 'Phytochrome' },
  ];
  const [hovered, setHovered] = useState<number | null>(null);
  const { ref, visible } = useOnScreen(0.2);

  return (
    <div ref={ref} className={`spectrum-vis ${visible ? 'spectrum-vis--visible' : ''}`}>
      <div className="spectrum-bars">
        {bands.map((b, i) => (
          <div key={i} className="spectrum-col"
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <div className="spectrum-bar-wrap" style={{ height: `${b.height}%`, transitionDelay: `${i * 60}ms` }}>
              <div className="spectrum-bar-fill" style={{
                background: b.color,
                boxShadow: hovered === i ? `0 0 20px ${b.color}, 0 0 40px ${b.color}40` : `0 0 8px ${b.color}60`,
              }}/>
              <span className="spectrum-pct">{b.height}%</span>
            </div>
            <span className="spectrum-nm" style={{ color: b.color }}>{b.nm}<small>nm</small></span>
            <span className="spectrum-name">{b.label}</span>
            <span className="spectrum-use">{b.use}</span>
            {hovered === i && (
              <div className="spectrum-tooltip" style={{ borderColor: b.color }}>
                <strong style={{ color: b.color }}>{b.label}</strong>
                <span>{b.use}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="spectrum-gradient"/>
      <div className="spectrum-legend">
        <span><i style={{ background: LED.blue }}/>Blue 430 to 450 nm: compact, leafy vegetative growth</span>
        <span><i style={{ background: LED.deepRed }}/>Red 630 to 660 nm: peak photosynthesis, flowering and fruit</span>
        <span><i style={{ background: LED.farRed }}/>Far red 730 nm: stem extension and flowering signal</span>
        <span><i style={{ background: LED.uvA }}/>UV-A 380 nm: flavour, colour and pest defence</span>
        <span className="spectrum-legend-note">Bar height is the relative share of each wavelength in the AgriVoLED growth recipe.</span>
      </div>
    </div>
  );
}

// ─── Stats Row (animated counters on scroll) ─────────────────────────────────
function StatsRow() {
  const { ref, visible } = useOnScreen(0.3);
  const stats = [
    { value: 22, suffix: '', label: 'Crops scored for local organic', color: LED.green },
    { value: 61, suffix: '', label: 'Growing techniques assessed', color: LED.blue },
    { value: 18, suffix: '', label: 'Growing systems modelled', color: LED.deepRed },
    { value: 12, suffix: '', label: 'LED wavelengths deployed', color: LED.uvA },
  ];
  return (
    <div ref={ref} className={`stats-row ${visible ? 'stats-row--visible' : ''}`}>
      {stats.map((s, i) => (
        <div key={i} className="stat-card" style={{ borderColor: `${s.color}30`, transitionDelay: `${i * 100}ms` }}>
          <span className="stat-value" style={{ color: s.color }}>
            <Counter end={s.value} suffix={s.suffix} />
          </span>
          <span className="stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Growing Systems Grid ────────────────────────────────────────────────────
function GrowingSystems() {
  const { ref, visible } = useOnScreen(0.15);
  const systems = [
    { name: 'No-dig Beds', yield: '6 kg/m\u00B2/yr', energy: '0.1 kWh/kg', organic: true, icon: '\uD83C\uDF31', color: LED.green },
    { name: 'Polytunnel', yield: '12 kg/m\u00B2/yr', energy: '0.3 kWh/kg', organic: true, icon: '\uD83C\uDFD5\uFE0F', color: LED.green },
    { name: 'Mushroom Room', yield: '110 kg/m\u00B2/yr', energy: '2.2 kWh/kg', organic: true, icon: '\uD83C\uDF44', color: LED.orange },
    { name: 'Microgreens Bench', yield: '150 kg/m\u00B2/yr', energy: '4.5 kWh/kg', organic: false, icon: '\uD83C\uDF3F', color: LED.blue },
    { name: 'Vertical Farm', yield: '95 kg/m\u00B2/yr', energy: '12 kWh/kg', organic: false, icon: '\uD83C\uDFED', color: LED.royalBlue },
    { name: 'Soil-bed Glasshouse', yield: '28 kg/m\u00B2/yr', energy: '4.5 kWh/kg', organic: true, icon: '\uD83C\uDFE1', color: LED.red },
    { name: 'Rooftop Farm', yield: '7 kg/m\u00B2/yr', energy: '0.2 kWh/kg', organic: true, icon: '\uD83C\uDF07', color: LED.yellow },
    { name: 'Agrivoltaic Array', yield: '0.42 kg/m\u00B2/yr', energy: 'Net gen', organic: true, icon: '\u2600\uFE0F', color: LED.deepRed },
  ];
  return (
    <div ref={ref} className={`gs-grid ${visible ? 'gs-grid--visible' : ''}`}>
      {systems.map((s, i) => (
        <div key={i} className="gs-card" style={{ borderColor: `${s.color}25`, transitionDelay: `${i * 60}ms` }}>
          <div className="gs-icon">{s.icon}</div>
          <div className="gs-info">
            <h4 style={{ color: s.color }}>{s.name}</h4>
            <div className="gs-meta">
              <span>{s.yield}</span>
              <span>{s.energy}</span>
              <span className={s.organic ? 'gs-organic' : 'gs-indoor'}>{s.organic ? 'Organic' : 'Indoor'}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Three Pillars (glassmorphism) ───────────────────────────────────────────
function Pillars() {
  const { ref, visible } = useOnScreen(0.15);
  const pillars = [
    { title: 'Digital Twin', desc: 'A live model of every site, crop, and batch. Many small sites run as one operation. Every organic, local, and low-carbon claim has records behind it.', color: LED.blue,
      img: '/images/11-detail-light-intensity-widget.png', icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/><circle cx="15" cy="15" r="2"/></svg>
    )},
    { title: 'Growing Sites', desc: 'No-dig beds, polytunnels, mushroom rooms, and rooftops. Soil-grown. Stock-free organic. Close to the people who eat the food.', color: LED.green,
      img: '/images/13-detail-controller-box.png', icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22V12"/><path d="M8 12c0-4 4-8 4-8s4 4 4 8"/><path d="M5 18c0-3 3-6 7-6M19 18c0-3-3-6-7-6"/></svg>
    )},
    { title: 'The Game', desc: 'A global strategy game built on the same data model. Build your own plant-based food company anywhere in the world.', color: LED.deepRed,
      img: undefined, icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4M8 10v4M16 11h.01M14 13h.01"/></svg>
    )},
  ];
  return (
    <div ref={ref} className={`pillars ${visible ? 'pillars--visible' : ''}`}>
      {pillars.map((p, i) => (
        <div key={i} className="pillar-glass" style={{ borderTopColor: p.color, transitionDelay: `${i * 120}ms` }}>
          <div className="pillar-glow" style={{ background: p.color }}/>
          {p.img ? (
            <img src={p.img} alt="" className="pillar-img"/>
          ) : (
            <div className="pillar-icon" style={{ color: p.color }}>{p.icon}</div>
          )}
          <h3>{p.title}</h3>
          <p>{p.desc}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Responsive picture: WebP + 800 px mobile variants (added 25 Oct 2026) ──
export function Pic({ src, alt, className, width, height, sizes = '(max-width: 760px) 100vw, 1600px', loading = 'lazy' }:
  { src: string; alt: string; className?: string; width?: number; height?: number; sizes?: string; loading?: 'lazy' | 'eager' }) {
  const base = src.replace(/\.jpg$/, '');
  const isJpg = src.endsWith('.jpg');
  if (!isJpg) return <img src={src} alt={alt} className={className} width={width} height={height} loading={loading}/>;
  return (
    <picture className="pic">
      <source type="image/webp" srcSet={`${base}-m.webp 800w, ${base}.webp ${width || 1584}w`} sizes={sizes}/>
      <img src={src} srcSet={`${base}-m.jpg 800w, ${src} ${width || 1584}w`} sizes={sizes} alt={alt} className={className} width={width} height={height} loading={loading} decoding="async"/>
    </picture>
  );
}

// ─── Top navigation (added 24 Oct 2026) ───────────────────────────────────
function TopNav({ onLogin, onRegister }: { onLogin: () => void; onRegister: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const go = (id: string) => () => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };
  return (
    <nav className={`topnav ${scrolled ? 'topnav--scrolled' : ''} ${open ? 'topnav--open' : ''}`}>
      <div className="topnav-inner">
        <button className="topnav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="topnav-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" width="30" height="30"><defs><linearGradient id="nvg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#16a34a"/><stop offset="1" stopColor="#0ea5e9"/></linearGradient></defs><rect x="2" y="2" width="28" height="28" rx="8" fill="url(#nvg)"/><path d="M16 24V13" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M16 15c-4-1-6-4-6-8 4 0 6 2 6 8zM16 18c0-5 2-7 6-7 0 4-2 7-6 7z" fill="#fff"/></svg>
          </span>
          <span className="topnav-name">Agri<span>VoLED</span></span>
        </button>
        <div className="topnav-links">
          <button onClick={go('light')}>Light</button>
          <button onClick={go('systems')}>Systems</button>
          <button onClick={go('pillars')}>Platform</button>
          <button onClick={go('gap')}>Why</button>
          <a href="/farm">Farm</a>
          <a href="/visuals">Visuals</a>
        </div>
        <div className="topnav-actions">
          <button className="topnav-login" onClick={onLogin}>Sign In</button>
          <button className="topnav-cta" onClick={onRegister}>Get Started</button>
          <button className="topnav-burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            <span/><span/><span/>
          </button>
        </div>
      </div>
    </nav>
  );
}

// ─── Sunlight in two ways: animated SVG flow (added 24 Oct 2026) ───────────
export function SunlightFlow() {
  const { ref, visible } = useOnScreen(0.25);
  const stages = [
    { x: 110,  label: 'Sunlight',       sub: 'Free, intermittent' },
    { x: 400,  label: 'Perovskite PV',  sub: 'Thin film, high yield' },
    { x: 640,  label: 'DC drive',       sub: 'No inverter losses' },
    { x: 900,  label: 'Tunable LED',    sub: 'Only useful wavelengths' },
    { x: 1110, label: 'Crops',          sub: 'Year-round, local' },
  ];
  return (
    <div ref={ref} className={`sunflow ${visible ? 'sunflow--visible' : ''}`}>
      <svg viewBox="0 0 1220 330" className="sunflow-svg" aria-label="Sunlight to perovskite PV to DC LED to crops">
        <defs>
          <radialGradient id="sfSun" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff7c2"/><stop offset="55%" stopColor="#fbbf24"/><stop offset="100%" stopColor="#f59e0b"/>
          </radialGradient>
          <linearGradient id="sfPv" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1e3a8a"/><stop offset="50%" stopColor="#312e81"/><stop offset="100%" stopColor="#6d28d9"/>
          </linearGradient>
          <linearGradient id="sfLed" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#9333ea"/><stop offset="35%" stopColor="#2563eb"/><stop offset="70%" stopColor="#ef4444"/><stop offset="100%" stopColor="#e91e3c"/>
          </linearGradient>
          <linearGradient id="sfCone" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c084fc" stopOpacity="0.45"/><stop offset="100%" stopColor="#fca5a5" stopOpacity="0"/>
          </linearGradient>
          <filter id="sfGlow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="6"/></filter>
        </defs>

        {/* connecting wire (dashed, animated) */}
        <path d="M500 150 C 560 150, 580 150, 600 150" fill="none" stroke="#2563eb" strokeWidth="3" strokeDasharray="8 8" className="sf-wire"/>
        <path d="M680 150 C 740 150, 760 150, 790 150" fill="none" stroke="#2563eb" strokeWidth="3" strokeDasharray="8 8" className="sf-wire"/>

        {/* Sun */}
        <g className="sf-sun" style={{ transformOrigin: '110px 150px' }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={i} x1="110" y1="88" x2="110" y2="70" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round"
              transform={`rotate(${i * 30} 110 150)`} opacity="0.8"/>
          ))}
        </g>
        <circle cx="110" cy="150" r="60" fill="#fbbf24" opacity="0.25" filter="url(#sfGlow)"/>
        <circle cx="110" cy="150" r="42" fill="url(#sfSun)"/>

        {/* photons sun -> panel */}
        {[0, 1, 2, 3, 4].map(i => (
          <circle key={i} r="4" fill="#fbbf24" opacity="0.9">
            <animateMotion dur="2.4s" begin={`${i * 0.45}s`} repeatCount="indefinite" path={`M160 ${120 + i * 15} L 330 ${110 + i * 18}`}/>
          </circle>
        ))}

        {/* Perovskite PV panel (tilted) */}
        <g className="sf-panel">
          <polygon points="330,100 500,80 500,200 330,220" fill="url(#sfPv)" stroke="#1e1b4b" strokeWidth="2"/>
          {[0, 1, 2, 3].map(i => (
            <line key={'h' + i} x1="330" y1={124 + i * 24} x2="500" y2={104 + i * 24} stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
          ))}
          {[0, 1, 2, 3, 4, 5].map(i => (
            <line key={'v' + i} x1={330 + i * 34} y1={100 - i * 4} x2={330 + i * 34} y2={220 - i * 4} stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
          ))}
          <polygon points="330,100 500,80 500,86 330,106" fill="rgba(255,255,255,0.35)"/>
          <rect x="400" y="222" width="30" height="12" rx="2" fill="#475569"/>
          <rect x="410" y="234" width="10" height="26" fill="#475569"/>
        </g>

        {/* DC driver box */}
        <g>
          <rect x="600" y="118" width="80" height="64" rx="12" fill="#fff" stroke="#2563eb" strokeWidth="2.5"/>
          <text x="640" y="144" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1e3a8a" fontFamily="Plus Jakarta Sans, system-ui">DC</text>
          <path d="M616 164 h14 l6 -10 l8 20 l6 -10 h14" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </g>

        {/* electrons along wires */}
        {[0, 1, 2].map(i => (
          <circle key={'e1' + i} r="4" fill="#2563eb">
            <animateMotion dur="1.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" path="M500 150 L 600 150"/>
          </circle>
        ))}
        {[0, 1, 2].map(i => (
          <circle key={'e2' + i} r="4" fill="#2563eb">
            <animateMotion dur="1.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" path="M680 150 L 790 150"/>
          </circle>
        ))}

        {/* LED bar with light cone */}
        <rect x="790" y="140" width="220" height="20" rx="10" fill="#1f2937"/>
        <rect x="796" y="146" width="208" height="8" rx="4" fill="url(#sfLed)" className="sf-ledbar"/>
        {Array.from({ length: 10 }).map((_, i) => (
          <circle key={'d' + i} cx={806 + i * 22} cy="150" r="2.5" fill="#fff" opacity="0.9" className="led-dot-blink" style={{ animationDelay: `${i * 0.1}s` }}/>
        ))}
        <polygon points="796,160 1004,160 1060,280 740,280" fill="url(#sfCone)"/>

        {/* Crops under the light */}
        {Array.from({ length: 9 }).map((_, j) => {
          const px = 790 + j * 27;
          const h = 26 + ((j * 7) % 16);
          return (
            <g key={'p' + j} className="vf-plant" style={{ animationDelay: `${j * 0.25}s` }}>
              <line x1={px} y1="285" x2={px} y2={285 - h} stroke="#2e7d32" strokeWidth="2"/>
              <ellipse cx={px - 7} cy={285 - h + 6} rx="8" ry="5" fill="#66bb6a" transform={`rotate(-25 ${px - 7} ${285 - h + 6})`}/>
              <ellipse cx={px + 7} cy={285 - h + 3} rx="8" ry="5" fill="#81c784" transform={`rotate(25 ${px + 7} ${285 - h + 3})`}/>
              <ellipse cx={px} cy={285 - h - 3} rx="6" ry="4.5" fill="#a5d6a7"/>
            </g>
          );
        })}
        <rect x="770" y="285" width="300" height="8" rx="4" fill="#a16207" opacity="0.5"/>

        {/* Stage labels */}
        {stages.map((s, i) => (
          <g key={i} className="sf-label" style={{ transitionDelay: `${i * 120}ms` }}>
            <text x={s.x} y="312" textAnchor="middle" fontSize="15" fontWeight="800" fill="#0f2b1a" fontFamily="Plus Jakarta Sans, system-ui">{s.label}</text>
            <text x={s.x} y="328" textAnchor="middle" fontSize="11" fill="#6b8f7a" fontFamily="Inter, system-ui">{s.sub}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Wave divider between sections (added 24 Oct 2026) ──────────────────────
function Wave({ from, to, flip = false }: { from: string; to: string; flip?: boolean }) {
  return (
    <div className="wave" style={{ background: from, transform: flip ? 'scaleX(-1)' : undefined }} aria-hidden="true">
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
        <path d="M0,30 C240,60 480,0 720,30 C960,60 1200,0 1440,30 L1440,60 L0,60 Z" fill={to}/>
      </svg>
    </div>
  );
}

// ─── Hero slideshow, all images, 10 s per slide (added 24 Oct 2026) ─────────
const SLIDES = [
  { src: '/images/02-hero-photo-solar-canopy.jpg', alt: 'Solar canopy over crops', title: 'Solar Canopy', desc: 'Sunlight used two ways: light for the crop, power for the LEDs' },
  { src: '/images/06-panel-adaptive-led-lighting.jpg', alt: 'Adaptive LED lighting', title: 'Adaptive LED Lighting', desc: 'Smart intensity control for every growth stage' },
  { src: '/images/07-panel-vertical-stacking.jpg', alt: 'Vertical stacking', title: 'Vertical Stacking', desc: 'Grow more in less space' },
  { src: '/images/09-panel-solar-powered-farming.jpg', alt: 'Solar-powered farming', title: 'Solar-Powered Farming', desc: 'Clean energy for continuous growth' },
  { src: '/images/08-panel-solar-integrated-building.jpg', alt: 'Solar-integrated building with daylight', title: 'Solar-Integrated Building', desc: 'Diffuse daylight through PV glazing, LED on the lower tiers' },
  { src: '/images/02b-hero-photo-solar-canopy-alt.jpg', alt: 'Solar canopy over crops, second view', title: 'Local Growing Sites', desc: 'Small sites under precision light, anywhere on Earth' },
  // 25 Oct 2026: the game hero moved out of here into GAME_SLIDES below
];
// Generic slideshow, extracted 25 Oct 2026 so the game page can have its own
// without duplicating the logic. `interval` is milliseconds between slides.
export type Slide = { src: string; alt: string; title: string; desc: string };

export function Slideshow({ slides, interval = 2000, label = 'Slideshow', href, hrefLabel }:
  { slides: Slide[]; interval?: number; label?: string; href?: string; hrefLabel?: string }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || slides.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [paused, slides.length, interval]);
  useEffect(() => { console.log(`[${label}] slide`, idx + 1, 'of', slides.length); }, [idx, slides.length, label]);
  return (
    <div className="slideshow" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="slideshow-stage">
        {slides.map((s, i) => (
          <Pic key={s.src} src={s.src} alt={s.alt} className={`slideshow-img ${i === idx ? 'slideshow-img--on' : ''}`} loading={i === 0 ? 'eager' : 'lazy'}/>
        ))}
        {href && (
          <a href={href} className="slideshow-link" aria-label={hrefLabel || `Go to ${href}`}>
            <span className="slideshow-link-hint" aria-hidden="true">Play the Game &rarr;</span>
          </a>
        )}
        <div className="slideshow-caption" key={idx}>
          <span className="slideshow-caption-title">{slides[idx].title}</span>
          <span className="slideshow-caption-desc">{slides[idx].desc}</span>
        </div>
        <button className="slideshow-arrow slideshow-arrow--prev" aria-label="Previous" onClick={() => setIdx(i => (i - 1 + slides.length) % slides.length)}>&#8249;</button>
        <button className="slideshow-arrow slideshow-arrow--next" aria-label="Next" onClick={() => setIdx(i => (i + 1) % slides.length)}>&#8250;</button>
      </div>
      <div className="slideshow-dots">
        {slides.map((_, i) => (
          <button key={i} className={`slideshow-dot ${i === idx ? 'slideshow-dot--on' : ''}`} aria-label={`Slide ${i + 1}`} onClick={() => setIdx(i)}>
            {i === idx && !paused && <span className="slideshow-progress" key={idx}/>}
          </button>
        ))}
      </div>
    </div>
  );
}

function HeroSlideshow() { return <Slideshow slides={SLIDES} label="HeroSlideshow"/>; }

// Game slideshow (moved here 25 Oct 2026 so both the landing page and /game
// can use it). The company slideshow above is SLIDES; this one is game-only.
export const GAME_SLIDES: Slide[] = [
  { src: '/images/19-game-hero.jpg',
    alt: 'Plot, site and city layers: a seedling in soil, a market garden with polytunnels and sensors, and a city with rooftop and vertical farms',
    title: 'Plot, Site, City',
    desc: 'Zoom from a single seedling to a whole city, and back again' },
  { src: '/images/20-game-agri-sync.jpg',
    alt: 'Soil-net diagnostics panel, a world map of growing regions, and deep-sea resource management',
    title: 'Land, Soil and Sea',
    desc: 'Soil-net diagnostics on one side, deep-sea resource management on the other' },
  { src: '/images/21-game-agri-sync-alt.jpg',
    alt: 'A control room view of a growing sphere, a vertical farming module cutaway, and a global network of desert and tundra growing domes',
    title: 'One Sphere, One Network',
    desc: 'Run a growing dome from the console, then watch the network spread across climates' },
];


// ─── Feature badges, vector versions of the composite's icon rows (added 24 Oct 2026) ──
const BADGE_ICONS: Record<string, React.ReactNode> = {
  leaf: <path d="M20 4c-8 0-14 5-14 13 0 1 .1 2 .3 3C9 16 13 13 18 11c-4 3-7 7-8.5 11C16 22 20 15 20 4z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>,
  battery: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="5" width="10" height="16" rx="2"/><path d="M10 3h4"/><path d="M13 10l-3 4h4l-3 4"/></g>,
  sprout: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21v-9"/><path d="M12 12c0-4 3-6 7-6 0 4-3 6-7 6z"/><path d="M12 15c0-3-2.5-5-6-5 0 3 2.5 5 6 5z"/><path d="M6 21h12"/></g>,
  thermo: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 14.5V5a2 2 0 0 1 4 0v9.5a3.5 3.5 0 1 1-4 0z"/><path d="M12 10v6"/></g>,
  bulb: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 21h4"/><path d="M8.5 14a5.5 5.5 0 1 1 7 0c-.8.7-1.5 1.6-1.5 2.5v.5h-4v-.5c0-.9-.7-1.8-1.5-2.5z"/></g>,
  chart: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16"/><path d="M5 16l4-5 3 3 4-6 3 3"/><path d="M16 11h3v3"/></g>,
};
const BADGES = [
  { icon: 'leaf',    label: 'Renewable Energy',    colour: '#16a34a' },
  { icon: 'battery', label: 'Energy Storage',      colour: '#0ea5e9' },
  { icon: 'sprout',  label: 'Continuous Growth',   colour: '#65a30d' },
  { icon: 'thermo',  label: 'Monitor Environment', colour: '#f59e0b' },
  { icon: 'bulb',    label: 'Control Lighting',    colour: '#9333ea' },
  { icon: 'chart',   label: 'Track Growth',        colour: '#e91e3c' },
];
export function FeatureBadges() {
  const { ref, visible } = useOnScreen(0.3);
  return (
    <div ref={ref} className={`badges ${visible ? 'badges--visible' : ''}`}>
      {BADGES.map((b, i) => (
        <div key={b.label} className="badge" style={{ transitionDelay: `${i * 90}ms`, ['--badge' as any]: b.colour }}>
          <span className="badge-ring"><svg viewBox="0 0 24 24" width="30" height="30">{BADGE_ICONS[b.icon]}</svg></span>
          <span className="badge-label">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Growing systems data table, shown beside the infographic (added 24 Oct 2026) ──
const SYSTEM_ROWS = [
  { name: 'No-dig Beds', yield: '6', energy: '0.1', tag: 'Organic', colour: LED.green, note: 'Outdoor soil beds, no tilling, compost mulch.' },
  { name: 'Polytunnel', yield: '12', energy: '0.3', tag: 'Organic', colour: LED.green, note: 'Unheated cover extends the season by weeks each end.' },
  { name: 'Mushroom Room', yield: '110', energy: '2.2', tag: 'Organic', colour: LED.orange, note: 'Dark, humid room on spent substrate; no light needed.' },
  { name: 'Microgreens Bench', yield: '150', energy: '4.5', tag: 'Indoor', colour: LED.blue, note: 'Shallow trays under LED, harvested in 7 to 14 days.' },
  { name: 'Vertical Farm', yield: '95', energy: '12', tag: 'Indoor', colour: LED.royalBlue, note: 'Stacked LED shelves; highest control, highest energy.' },
  { name: 'Soil-bed Glasshouse', yield: '28', energy: '4.5', tag: 'Organic', colour: LED.red, note: 'Glass structure, daylight plus supplemental LED.' },
  { name: 'Rooftop Farm', yield: '7', energy: '0.2', tag: 'Organic', colour: LED.yellow, note: 'Containers and raised beds on unused roof space.' },
  { name: 'Agrivoltaic Array', yield: '0.42', energy: 'Net gen', tag: 'Organic', colour: LED.deepRed, note: 'Grazing or low crops beneath PV; exports electricity.' },
];
function SystemsTable() {
  const { ref, visible } = useOnScreen(0.2);
  return (
    <div ref={ref} className={`systems-table ${visible ? 'systems-table--visible' : ''}`}>
      <div className="systems-table-head">
        <span>System</span><span>Yield<small>kg/m²/yr</small></span><span>Energy<small>kWh/kg</small></span><span>Type</span>
      </div>
      {SYSTEM_ROWS.map((r, i) => (
        <div key={r.name} className="systems-row" style={{ transitionDelay: `${i * 60}ms`, ['--row' as any]: r.colour }}>
          <div className="systems-name"><strong>{r.name}</strong><span>{r.note}</span></div>
          <span className="systems-num">{r.yield}</span>
          <span className="systems-num">{r.energy}</span>
          <span className={`systems-tag ${r.tag === 'Organic' ? 'systems-tag--organic' : 'systems-tag--indoor'}`}>{r.tag}</span>
        </div>
      ))}
      <p className="systems-foot">Yield is edible crop per square metre of floor per year. Energy is electricity per kilogram harvested. The agrivoltaic array generates more electricity than it uses.</p>
    </div>
  );
}

// ─── PAR badge and spectrum bar, moved off the home banner (24 Oct 2026) ─────
export function ParBadge() {
  return (
    <div className="hero-badge">
      <span className="hero-badge-dot"/>
      <span>Photosynthetically Active Radiation</span>
    </div>
  );
}
export function SpectrumBar() {
  return (
      <div className="hero-spectrum">
        <div className="hero-spectrum-track">
          <div style={{ background: LED.uvA, flex: 1 }}/>
          <div style={{ background: LED.royalBlue, flex: 1.2 }}/>
          <div style={{ background: LED.blue, flex: 1.5 }}/>
          <div style={{ background: LED.green, flex: 1 }}/>
          <div style={{ background: LED.yellow, flex: 0.8 }}/>
          <div style={{ background: LED.orange, flex: 0.8 }}/>
          <div style={{ background: LED.red, flex: 1.5 }}/>
          <div style={{ background: LED.deepRed, flex: 1.2 }}/>
          <div style={{ background: LED.farRed, flex: 0.5 }}/>
        </div>
        <div className="hero-spectrum-labels">
          <span>380nm</span><span>450nm</span><span>520nm</span><span>630nm</span><span>730nm</span>
        </div>
      </div>
  );
}

// ─── Auth Modal ──────────────────────────────────────────────────────────────
function AuthModal({ mode, onClose }: { mode: 'login' | 'register'; onClose: () => void }) {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password || (!isLogin && !name)) { setError('Please fill all fields'); return; }
    setError(''); setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      onClose();
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-md" onClick={onClose}/>
      <div className="auth-modal">
        <button onClick={onClose} className="auth-close"><X className="w-5 h-5" /></button>
        <div className="text-center mb-6">
          <div className="auth-logo">A</div>
          <h2 className="text-2xl font-black mt-4">{isLogin ? 'Welcome back' : 'Join AgriVoLED'}</h2>
          <p className="text-sm mt-1">
            {isLogin ? 'Continue building your network' : 'Start growing local food anywhere'}
          </p>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <div className="space-y-3">
          {!isLogin && (
            <div>
              <label className="auth-label">Full Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="auth-input" placeholder="Your name"/>
            </div>
          )}
          <div>
            <label className="auth-label">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} className="auth-input" placeholder="your@email.com"/>
          </div>
          <div>
            <label className="auth-label">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} className="auth-input" placeholder="********"/>
          </div>
          <button onClick={handleSubmit} disabled={loading} className="auth-btn">
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
          <p className="text-center text-sm mt-4">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="font-semibold hover:opacity-80" style={{ color: LED.blue }}>
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN LANDING
// ═════════════════════════════════════════════════════════════════════════════

export default function Landing() {
  const [authModal, setAuthModal] = useState<'login' | 'register' | null>(null);

  return (
    <div className="landing-root">
      <LedParticles />
      <TopNav onLogin={() => setAuthModal('login')} onRegister={() => setAuthModal('register')} />

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section className="hero">
        {/* hero background photo retired 24 Oct 2026: the slideshow below carries the images */}
        <div className="hero-content">
          <div className="hero-left">
            {/* PAR badge moved to the /farm page on 24 Oct 2026 */}
            <h1 className="hero-title">
              <span>Agri</span><span className="hero-voled">VoLED</span>
            </h1>
            <p className="hero-tagline">Grow local food. Anywhere on Earth.</p>
            <p className="hero-sub">
              A network of small growing sites under precision LED light. A digital twin of every crop and batch.
              A strategy game where you build your own food company. Soil-grown. Stock-free. Every claim proven.
            </p>

            {/* Spectrum bar moved to the /farm page on 24 Oct 2026 (see SpectrumBar) */}

            {/* Sign In / Get Started moved under the slideshow on 24 Oct 2026 */}
          </div>
          <div className="hero-right hero-right--retired">
            <div className="hero-tower-wrap hero-tower-wrap--photo-only">
              {/* VerticalFarmTower moved to the /farm page on 24 Oct 2026 */}
              <div className="hero-photo-card">
                <img src="/images/07-panel-vertical-stacking.jpg" width="266" height="137" alt="Vertical stacking — grow more in less space"/>
                <span>Vertical stacking</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SLIDESHOW (added 24 Oct 2026) ─────────────────────────────────── */}
      <section className="section section-slideshow">
        <HeroSlideshow />
        {/* Sign In / Get Started removed from under the slideshow 25 Oct 2026;
            they remain in the top navigation */}
        {/* GAME SLIDESHOW ON THE LANDING PAGE (added 25 Oct 2026) — separate
            from the company slideshow above; same component, its own images */}
        <div className="game-strip">
          <h3 className="game-strip-title">A Global Strategy Game</h3>
          <Slideshow slides={GAME_SLIDES} label="LandingGameSlideshow"
                     href="/game" hrefLabel="Open the game home page"/>
        </div>

        {/* GAME CTA, simplified 25 Oct 2026 — just a big wide button under the
            game slideshow; the chip, heading and copy were removed */}
        <a href="/game" className="game-cta-btn game-cta-btn--wide">
          <span className="game-cta-btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
              <path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.5-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5z"/>
            </svg>
          </span>
          Play the Game
        </a>
        {/* FeatureBadges moved to the /farm page on 25 Oct 2026 */}
      </section>

      {/* ── STATS ──────────────────────────────────────────────────────────── */}
      <section className="section section-stats">
        <StatsRow />
      </section>

      {/* Sunlight flow animation moved to the /visuals page on 24 Oct 2026 */}

      {/* Collage banner moved to the /visuals page on 24 Oct 2026 */}

      {/* ── SPECTRUM SECTION ───────────────────────────────────────────────── */}
      <section className="section section-spectrum" id="light">
        <div className="section-header">
          <h2 className="section-title gradient-text">The Light That Grows</h2>
          <p className="section-sub">Every wavelength has a job. Every LED is chosen for what it does to the plant.</p>
        </div>
        <SpectrumVisualiser />
      </section>

      {/* Photo showcase retired 24 Oct 2026: its captions now ride on the slideshow */}

      {/* ── GROWING SYSTEMS ────────────────────────────────────────────────── */}
      <section className="section section-systems" id="systems">
        <div className="section-header">
          <h2 className="section-title gradient-text-green">Where You Grow Decides What You Can Claim</h2>
          <p className="section-sub">Eighteen growing systems assessed. Organic certification requires soil. Energy decides indoor viability.</p>
        </div>
        {/* GrowingSystems card grid replaced by the uploaded infographic on 24 Oct 2026; component kept */}
        <div className="systems-layout">
          <figure className="systems-infographic">
            <Pic src="/images/18-growing-systems-infographic.jpg" width={688} height={1529} sizes="(max-width: 1000px) 90vw, 420px" alt="Eight growing systems compared: yield, energy per kilogram and organic or indoor status"/>
          </figure>
          <SystemsTable />
        </div>
      </section>

      {/* ── THREE PILLARS ──────────────────────────────────────────────────── */}
      <section className="section section-pillars" id="pillars">
        <div className="section-header">
          <h2 className="section-title gradient-text-warm">Three Building Blocks</h2>
          <p className="section-sub">Not one large farm far away. Many small sites, one shared model, one strategy engine.</p>
        </div>
        <Pillars />
      </section>

      {/* ── IMPORT GAP ─────────────────────────────────────────────────────── */}
      <section className="section section-gap" id="gap">
        <div className="gap-card">
          <div className="gap-left">
            <h2>Most countries import <span style={{ color: LED.deepRed }}>more food</span> than they need to</h2>
            <p>Tomatoes, garlic, peppers, herbs, salads — everyday vegetables shipped thousands of miles when they could be grown locally under LED light, on soil, year-round.</p>
            <p style={{ color: LED.green, fontWeight: 600, marginTop: 16 }}>AgriVoLED is a plan to close that gap — starting where you are.</p>
          </div>
          <div className="gap-right">
            <div className="gap-stat">
              <span className="gap-num" style={{ color: LED.deepRed }}>80%</span>
              <span className="gap-desc">Of food miles are avoidable</span>
            </div>
            <div className="gap-stat">
              <span className="gap-num" style={{ color: LED.blue }}>22</span>
              <span className="gap-desc">Crops scored for local growing</span>
            </div>
            <div className="gap-stat">
              <span className="gap-num" style={{ color: LED.green }}>61</span>
              <span className="gap-desc">Growing techniques assessed</span>
            </div>
            <div className="gap-stat">
              <span className="gap-num" style={{ color: LED.uvA }}>4</span>
              <span className="gap-desc">Trade tables compiled</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="footer-spectrum"/>
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-name">Agri<span>VoLED</span></span>
            <span className="footer-tag">Grow local food. Anywhere on Earth.</span>
          </div>
          <div className="footer-links">
            <button onClick={() => document.getElementById('light')?.scrollIntoView({ behavior: 'smooth' })}>Light</button>
            <button onClick={() => document.getElementById('systems')?.scrollIntoView({ behavior: 'smooth' })}>Systems</button>
            <button onClick={() => document.getElementById('pillars')?.scrollIntoView({ behavior: 'smooth' })}>Platform</button>
            <a href="/farm">Farm</a>
            <a href="/visuals">Visuals</a>
            <button onClick={() => setAuthModal('register')}>Get Started</button>
          </div>
          <span className="footer-copy">AgriVoLED, {new Date().getFullYear()}</span>
        </div>
      </footer>

      {authModal && <AuthModal mode={authModal} onClose={() => setAuthModal(null)} />}
    </div>
  );
}
